import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  getGapAnalysisControlDetails,
  updateStatusControl,
  updateMaturityLevel,
  updateStatusObjectiveControl,
} from '../../../../services/gap-analysis';
import { PageLoaderShow } from '../../../../Components/PageLoader/Show';
import { Main as Container } from 'Components/Main';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../../store/auth';
import { getCompanyById } from '../../../../services/company-service';
import { getAssetById } from '../../../../services/assets-service';
import { getAreaById } from '../../../../services/areas-service';
import { getProcessById } from '../../../../services/processes-service';
import { getSystemById } from '../../../../services/systems-service';
import { typeLabelKeys, getSolutionKey, statusControl, maturityLevelKeys, translateDomain, translateFunctionGrouping, translateApplicability } from '../../../../utils/labels_utils';
import { SnackbarAlert } from 'Components/common/SnackbarAlert';
import { PageHeader, Select } from 'Components/ui';
import RequirementLabel from '../../../../Components/RequirementLabel';
import styled from 'styled-components';

const entityFetchMap = {
  companies: getCompanyById,
  areas: getAreaById,
  processes: getProcessById,
  systems: getSystemById,
  assets: getAssetById,
};

// ─── Design tokens ───────────────────────────────────────────────────────────
const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';
const BORDER_IDLE = 'var(--vo-steel)';
const DIVIDER = 'var(--vo-cloud)';

// ─── Cards ───────────────────────────────────────────────────────────────────
const Card = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 24px;
  margin-bottom: 16px;
`;
const StatusCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 20px 24px;
  margin-bottom: 16px;
  border-left: 4px solid ${p => p.color || BORDER_IDLE};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;
const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${DIVIDER};
`;

// ─── Status bar ──────────────────────────────────────────────────────────────
const StatusMeta = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 2px;
`;
const StatusValue = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.color || MUTED};
  margin: 0;
`;
// ─── Meta fields ─────────────────────────────────────────────────────────────
const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: ${p => p.mb || '0'};
`;
const MetaItem = styled.div``;
const MetaLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 5px;
`;
const MetaText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: ${p => p.size || '14px'};
  font-weight: ${p => p.weight || '500'};
  color: ${p => p.color || INK};
  margin: 0;
  line-height: ${p => p.lh || '1.4'};
`;
const MetaBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.color};
  background: ${p => p.bg};
  padding: 3px 10px;
  border-radius: ${p => p.pill ? '20px' : '10px'};
  display: inline-block;
`;
const MetaWeight = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`;

// ─── Section blocks ───────────────────────────────────────────────────────────
const SectionBlock = styled.div`
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
`;
const SectionLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 6px;
`;
const SectionText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  margin: 0;
  line-height: 1.65;
  white-space: pre-line;
`;
const RequirementFrameworkGroup = styled.div`
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
`;
const RequirementFrameworkName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${TEXT};
  margin: 0 0 6px;
`;
const RequirementList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// ─── Accordion ────────────────────────────────────────────────────────────────
const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const AccordionItem = styled.div`
  border: 1px solid ${DIVIDER};
  border-radius: 10px;
  overflow: hidden;
`;
const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: var(--vo-capri-25);
  cursor: pointer;
  &:hover { background: var(--vo-capri-50); }
`;
const AccordionText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  flex: 1;
  line-height: 1.45;
`;
const ObjBadge = styled.span`
  display: inline-flex;
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.color};
  background: ${p => p.bg};
  padding: 2px 9px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
`;
const ObjDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.color};
  flex-shrink: 0;
`;
const AccordionBody = styled.div`
  padding: 16px;
  border-top: 1px solid var(--vo-mist);
`;
const ObjIdLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 10px;
`;
// ─── Maturity card ────────────────────────────────────────────────────────────
const MaturityBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 480px;
`;
const MaturityLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
  display: block;
  margin-bottom: 6px;
`;
const MaturityDesc = styled.div`
  background: ${BG};
  border-radius: 10px;
  padding: 14px 16px;
`;
const MaturityDescTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 6px;
`;
const MaturityDescText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  margin: 0;
  line-height: 1.65;
  white-space: pre-line;
`;
const SaveBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  &:hover { background: ${BLUE_DEEP}; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const CRIT_STYLES = {
  '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  '2': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
};

// criticality i18n labels live under different namespaces per entity type
const criticalityLabelKey = {
  companies: 'asm:criticality.short',
  areas: 'asm:criticality.short',
  assets: 'asm:criticality.short',
  processes: 'mapping:processes.criticality.short',
  systems: 'mapping:systems.criticality.short',
};

// companies have no criticity field of their own — always treat as highest criticality
const COMPANY_CRITICITY = 5;

const getCriticalityInfo = (entityType, level, t) => {
  const styles = CRIT_STYLES[String(level)] || { color: MUTED, bg: 'var(--vo-slate-10)' };
  const keyBase = criticalityLabelKey[entityType];
  const short = keyBase ? t(`${keyBase}.${level}`, '') : '';
  return { ...styles, label: short ? `${short} (${level})` : String(level) };
};

const getObjectiveStatusStyle = (status) => {
  switch (status) {
    case 'attended':     return { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' };
    case 'notAttended':  return { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' };
    case 'notApplicable': return { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' };
    default:             return { color: 'var(--vo-steel)', bg: 'color-mix(in srgb, var(--vo-steel) 10%, transparent)' };
  }
};

// ─────────────────────────────────────────────────────────────────────────────

export const GapAnalysisControlDetalShow = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const { id } = useParams();
  const selectedTenant = useSelector(useAuthTenant);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [statuses, setStatuses] = useState({});
  const [currentLevel, setCurrentLevel] = useState('notPerformed');
  const [selectedStatus, setSelectedStatus] = useState('noResponse');
  const [entityData, setEntityData] = useState(null);
  const [expandedObjectives, setExpandedObjectives] = useState({});

  const { isLoading, error, data: control } = useQuery(
    ['getGapAnalysisControlDetails', id],
    async () => {
      const data = await getGapAnalysisControlDetails(id);
      return data.status === 200 ? data.data : null;
    }
  );

  useEffect(() => {
    if (control?.objectives) {
      setStatuses(control.objectives.reduce((acc, obj) => ({ ...acc, [obj._id]: obj.status }), {}));
    }
    if (control?.maturity_level) setCurrentLevel(control.maturity_level);
    if (control?.status) setSelectedStatus(control.status);
    if (control?.entity_type && control?.entity_id) {
      const fetchFn = entityFetchMap[control.entity_type];
      if (fetchFn) {
        fetchFn(control.entity_id)
          .then(response => setEntityData(response.data))
          .catch(err => console.error('Erro ao buscar entidade:', err));
      }
    }
  }, [control]);

  const handleUpdateMaturityLevel = async () => {
    if (!currentLevel) return;
    try {
      await updateMaturityLevel(id, currentLevel);
      setSnackbar({ open: true, message: t('gap-analysis:controlDetail.messages.maturitySuccess'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('gap-analysis:controlDetail.messages.maturityError'), severity: 'error' });
    }
  };

  const handleStatusControlChange = async (newStatus) => {
    setSelectedStatus(newStatus);
    try {
      await updateStatusControl(id, newStatus);
      setSnackbar({ open: true, message: t('gap-analysis:controlDetail.messages.statusSuccess'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('gap-analysis:controlDetail.messages.statusError'), severity: 'error' });
    }
  };

  const handleStatusChange = async (idObjective, newStatus) => {
    setStatuses(prev => ({ ...prev, [idObjective]: newStatus }));
    try {
      await updateStatusObjectiveControl(idObjective, newStatus);
      setSnackbar({ open: true, message: t('gap-analysis:controlDetail.messages.objectiveSuccess'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('gap-analysis:controlDetail.messages.objectiveError'), severity: 'error' });
    }
  };

  const toggleObjective = (objId) => {
    setExpandedObjectives(prev => ({ ...prev, [objId]: !prev[objId] }));
  };

  if (isLoading) return <PageLoaderShow />;
  if (error) return <div>{t('gap-analysis:controlsLink.error')}</div>;

  const cd = control.controlDetail;
  const employeeSize = parseFloat(selectedTenant.size);
  const solutionKey = getSolutionKey(employeeSize);
  const currentStatusInfo = statusControl[selectedStatus] || statusControl.noResponse;
  const statusColor = currentStatusInfo?.color || BORDER_IDLE;
  const statusLabel = currentStatusInfo?.labelKey ? t(currentStatusInfo.labelKey) : selectedStatus;
  const critInfo = control.entity_type === 'companies'
    ? getCriticalityInfo('companies', COMPANY_CRITICITY, t)
    : (entityData?.criticity ? getCriticalityInfo(control.entity_type, entityData.criticity, t) : null);
  const typeLabel = typeLabelKeys[control.entity_type] ? t(typeLabelKeys[control.entity_type]) : control.entity_type;
  const currentMaturityName = maturityLevelKeys[currentLevel] ? t(maturityLevelKeys[currentLevel]) : currentLevel;

  return (
    <>
      <Container>
        {/* Page header */}
        <PageHeader
          title={cd.scfControl}
          segments={[
            { label: t('common:breadcrumbs.analysis') },
            { label: t('gap-analysis:page.title'), route: '/gap-analysis' },
            { label: cd.scfControl },
          ]}
        />

        {/* 1 — Status bar card */}
        <StatusCard color={statusColor}>
          <div>
            <StatusMeta>{t('gap-analysis:controlDetail.statusLabel')}</StatusMeta>
            <StatusValue color={statusColor}>{statusLabel}</StatusValue>
          </div>
          <Select
            name="statusControl"
            value={selectedStatus}
            onChange={handleStatusControlChange}
            fullWidth={false}
            sx={{ minWidth: 200 }}
            options={Object.entries(statusControl)
              .filter(([key]) => key !== 'noEntities')
              .map(([key, value]) => ({ value: key, label: t(value.labelKey) }))}
          />
        </StatusCard>

        {/* 2 — Entity card */}
        <Card>
          <CardTitle>{t('gap-analysis:controlDetail.entityDetails')}</CardTitle>
          <MetaRow mb="16px">
            <MetaItem>
              <MetaLabel>{t('gap-analysis:controlDetail.fields.name')}</MetaLabel>
              <MetaText size="14px" weight="500">{entityData?.name || '—'}</MetaText>
            </MetaItem>
            {critInfo && (
              <MetaItem>
                <MetaLabel>{t('gap-analysis:controlDetail.fields.criticality')}</MetaLabel>
                <MetaBadge pill color={critInfo.color} bg={critInfo.bg}>
                  {critInfo.label}
                </MetaBadge>
              </MetaItem>
            )}
            <MetaItem>
              <MetaLabel>{t('gap-analysis:controlDetail.fields.type')}</MetaLabel>
              <MetaBadge pill color={BLUE_DEEP} bg="var(--vo-cobalto-8)">{typeLabel}</MetaBadge>
            </MetaItem>
          </MetaRow>
          <MetaItem style={{ width: '100%' }}>
            <MetaLabel>{t('gap-analysis:controlDetail.fields.description')}</MetaLabel>
            <MetaText size="13px" weight="400" color={MUTED} lh="1.6">
              {entityData?.description || t('common:empty.noDescription')}
            </MetaText>
          </MetaItem>
        </Card>

        {/* 3 — Control overview card */}
        <Card>
          <CardTitle>
            {t('gap-analysis:controlDetail.overview')} — {cd.scfControl}
          </CardTitle>
          <MetaRow mb="16px">
            <MetaItem>
              <MetaLabel>{t('gap-analysis:controlDetail.fields.domain')}</MetaLabel>
              <MetaBadge color={BLUE_DEEP} bg="var(--vo-cobalto-8)">{translateDomain(t, cd.scfDomain)}</MetaBadge>
            </MetaItem>
            <MetaItem>
              <MetaLabel>{t('gap-analysis:controlDetail.fields.function')}</MetaLabel>
              <MetaText size="13px" weight="400" color={TEXT}>{translateFunctionGrouping(t, cd.nistCsfFunctionGrouping)}</MetaText>
            </MetaItem>
            <MetaItem>
              <MetaLabel>{t('gap-analysis:controlDetail.fields.applicability')}</MetaLabel>
              <MetaText size="13px" weight="400" color={TEXT}>{translateApplicability(t, cd.applicabilityStatus)}</MetaText>
            </MetaItem>
            <MetaItem>
              <MetaLabel>{t('gap-analysis:controlDetail.fields.weight')}</MetaLabel>
              <MetaWeight>{cd.controlWeighting}</MetaWeight>
            </MetaItem>
          </MetaRow>

          <SectionBlock>
            <SectionLabel>{t('gap-analysis:controlDetail.fields.controlDesc')}</SectionLabel>
            <SectionText>{cd.scfControlDescription}</SectionText>
          </SectionBlock>

          {control.frameworkDetails?.some(fw => cd.frameworks?.[fw._id]?.length) && (
            <SectionBlock>
              <SectionLabel>{t('gap-analysis:controlDetail.fields.requirements')}</SectionLabel>
              {control.frameworkDetails
                .filter(fw => cd.frameworks?.[fw._id]?.length)
                .map(fw => (
                  <RequirementFrameworkGroup key={fw._id}>
                    <RequirementFrameworkName>{fw.sourceIdentifier}</RequirementFrameworkName>
                    <RequirementList>
                      {(fw.requirements || []).map(req => (
                        <RequirementLabel key={req.code} code={req.code} title={req.title} />
                      ))}
                    </RequirementList>
                  </RequirementFrameworkGroup>
                ))}
            </SectionBlock>
          )}

          {cd[solutionKey] && (
            <SectionBlock>
              <SectionLabel>{t('gap-analysis:controlDetail.fields.methods')}</SectionLabel>
              <SectionText>{cd[solutionKey]}</SectionText>
            </SectionBlock>
          )}
        </Card>

        {/* 4 — Objectives accordion */}
        <Card>
          <CardTitle>{t('gap-analysis:controlDetail.objectives')}</CardTitle>
          <AccordionList>
            {control.objectives.map(obj => {
              const status = statuses[obj._id] || 'notResponded';
              const style = getObjectiveStatusStyle(status);
              const objStatusLabel = (() => {
                switch (status) {
                  case 'attended':     return t('gap-analysis:controlDetail.status.attended');
                  case 'notAttended':  return t('gap-analysis:controlDetail.status.notAttended');
                  case 'notApplicable': return t('gap-analysis:controlDetail.status.notApplicable');
                  default:             return t('gap-analysis:controlDetail.status.notAnswered');
                }
              })();
              const expanded = !!expandedObjectives[obj._id];
              return (
                <AccordionItem key={obj._id}>
                  <AccordionHeader onClick={() => toggleObjective(obj._id)}>
                    <AccordionText>{obj.details.scfAssessmentObjective}</AccordionText>
                    <ObjBadge color={style.color} bg={style.bg}>{objStatusLabel}</ObjBadge>
                    <ObjDot color={style.color} />
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="var(--vo-steel)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      style={{ transition: 'transform 200ms', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </AccordionHeader>
                  {expanded && (
                    <AccordionBody>
                      <ObjIdLabel>ID: {obj.details.scfAoId}</ObjIdLabel>
                      <Select
                        name={`objStatus-${obj._id}`}
                        value={statuses[obj._id] || 'notResponded'}
                        onChange={value => handleStatusChange(obj._id, value)}
                        options={[
                          { value: 'notResponded', label: t('gap-analysis:controlDetail.status.notAnswered') },
                          { value: 'attended', label: t('gap-analysis:controlDetail.status.attended') },
                          { value: 'notAttended', label: t('gap-analysis:controlDetail.status.notAttended') },
                          { value: 'notApplicable', label: t('gap-analysis:controlDetail.status.notApplicable') },
                        ]}
                      />
                    </AccordionBody>
                  )}
                </AccordionItem>
              );
            })}
          </AccordionList>
        </Card>

        {/* 5 — Maturity level card */}
        <Card>
          <CardTitle>{t('gap-analysis:controlDetail.maturityLevelTitle')}</CardTitle>
          <MaturityBody>
            <div>
              <MaturityLabel>{t('gap-analysis:controlDetail.maturityCurrentLabel')}</MaturityLabel>
              <Select
                name="maturityLevel"
                value={currentLevel}
                onChange={setCurrentLevel}
                options={Object.entries(maturityLevelKeys).map(([key, transKey]) => ({ value: key, label: t(transKey) }))}
              />
            </div>

            {cd[currentLevel] && (
              <MaturityDesc>
                <MaturityDescTitle>{currentMaturityName}</MaturityDescTitle>
                <MaturityDescText>{cd[currentLevel]}</MaturityDescText>
              </MaturityDesc>
            )}

            <SaveBtn onClick={handleUpdateMaturityLevel}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
              {t('gap-analysis:controlDetail.updateMaturityBtn')}
            </SaveBtn>
          </MaturityBody>
        </Card>

      </Container>

      <SnackbarAlert
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </>
  );
};
